(function () {
  if (customElements.get('quantity-input')) {
    return;
  }

  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleSettled = this.handleSettled.bind(this);
      this.handleInventoryHydrated = this.handleInventoryHydrated.bind(this);
      this.settledUnsubscribe = null;
      this.inventoryUnsubscribe = null;
      this.isPending = false;
      this.numberInput = null;
      // Latest user-intended quantity captured while a mutate is in flight.
      // Replayed on `cart:line-mutate-settled` so rapid clicks aren't dropped.
      this.pendingDesired = null;
    }

    connectedCallback() {
      this.numberInput = this.querySelector('input[type="number"]');
      this.addEventListener('click', this.handleClick);
      if (this.numberInput) {
        this.numberInput.addEventListener('change', this.handleInputChange);
      }
      if (window.themePubsub && typeof window.themePubsub.subscribe === 'function') {
        this.settledUnsubscribe = window.themePubsub.subscribe(
          'cart:line-mutate-settled',
          this.handleSettled
        );
        this.inventoryUnsubscribe = window.themePubsub.subscribe(
          'cart:inventory-hydrated',
          this.handleInventoryHydrated
        );
      }
      this.applyMaxState();
    }

    applyMaxState() {
      const max = parseInt(this.dataset.cartMax || '', 10);
      const qty = parseInt(this.dataset.cartQty || '', 10);
      if (Number.isNaN(max) || max <= 0 || Number.isNaN(qty)) {
        return;
      }
      const plusButton =
        this.querySelector('button[data-qty-action="increase"]') ||
        this.querySelector('button[data-cart-qty="' + (qty + 1) + '"]');
      if (plusButton) {
        plusButton.disabled = qty >= max;
      }
      if (this.numberInput) {
        this.numberInput.max = String(max);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (this.numberInput) {
        this.numberInput.removeEventListener('change', this.handleInputChange);
        this.numberInput = null;
      }
      if (this.settledUnsubscribe) {
        this.settledUnsubscribe();
        this.settledUnsubscribe = null;
      }
      if (this.inventoryUnsubscribe) {
        this.inventoryUnsubscribe();
        this.inventoryUnsubscribe = null;
      }
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) {
        return;
      }
      event.preventDefault();
      if (button.disabled) {
        return;
      }

      if (this.numberInput && button.dataset.qtyAction) {
        // While pending, advance from the buffered desired qty instead of the
        // input's current value — keeps + + + clicks additive instead of stuck.
        const baseValue = this.pendingDesired != null
          ? this.pendingDesired
          : (parseInt(this.numberInput.value || '0', 10) || 0);
        const next = button.dataset.qtyAction === 'increase'
          ? baseValue + 1
          : Math.max(0, baseValue - 1);
        const max = parseInt(this.dataset.cartMax || '', 10);
        if (!Number.isNaN(max) && max > 0 && next > max) {
          button.disabled = true;
          return;
        }
        this.numberInput.value = String(next);
        if (this.isPending) {
          this.pendingDesired = next;
          return;
        }
        this.numberInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const quantity = parseInt(button.dataset.cartQty, 10);
      const key = this.dataset.cartKey;
      if (!key || Number.isNaN(quantity)) {
        return;
      }
      const max = parseInt(this.dataset.cartMax || '', 10);
      if (!Number.isNaN(max) && max > 0 && quantity > max) {
        button.disabled = true;
        return;
      }
      if (this.isPending) {
        this.pendingDesired = quantity;
        return;
      }
      this.dispatchMutate(key, quantity);
    }

    handleInputChange() {
      if (!this.numberInput) {
        return;
      }
      const key = this.dataset.cartKey;
      if (!key) {
        return;
      }
      const raw = parseInt(this.numberInput.value || '0', 10);
      const normalized = Number.isNaN(raw) ? 0 : Math.max(0, raw);
      const max = parseInt(this.dataset.cartMax || '', 10);
      const next = !Number.isNaN(max) && max > 0 ? Math.min(normalized, max) : normalized;
      if (next !== normalized) {
        this.numberInput.value = String(next);
      }
      this.dispatchMutate(key, next);
    }

    dispatchMutate(key, quantity) {
      this.isPending = true;
      this.classList.add('is-pending');
      this.dispatchEvent(
        new CustomEvent('cart:line-mutate', {
          bubbles: true,
          detail: { key: key, quantity: quantity }
        })
      );
    }

    handleSettled(payload) {
      if (!payload || payload.key !== this.dataset.cartKey) {
        return;
      }
      this.isPending = false;
      this.classList.remove('is-pending');

      if (this.pendingDesired != null) {
        const desired = this.pendingDesired;
        this.pendingDesired = null;
        const key = this.dataset.cartKey;
        if (key && !Number.isNaN(desired)) {
          this.dispatchMutate(key, desired);
        }
      }
    }

    handleInventoryHydrated(payload) {
      if (!payload || payload.handle !== this.dataset.productHandle) {
        return;
      }
      const variantMap = payload.variantMap;
      if (!variantMap || typeof variantMap.get !== 'function') {
        return;
      }
      const variantId = this.dataset.variantId;
      if (!variantId) {
        return;
      }
      const qty = parseInt(this.dataset.cartQty, 10);
      if (Number.isNaN(qty)) {
        return;
      }
      const cap = variantMap.get(String(variantId));
      if (cap == null) {
        return;
      }
      const increaseButton = this.querySelector('button[data-qty-action="increase"]');
      const plusButton = increaseButton || this.querySelector(
        'button[data-cart-qty="' + (qty + 1) + '"]'
      );
      this.dataset.cartMax = String(cap);
      if (plusButton) {
        plusButton.disabled = qty >= cap;
      }
      if (this.numberInput) {
        this.numberInput.max = String(cap);
        this.numberInput.dataset.cartMax = String(cap);
      }
    }
  }

  customElements.define('quantity-input', QuantityInput);
})();
